import { useEffect, useMemo, useRef, useState } from "react";
import { focusEvents, hotList, picks } from "@/mocks/campus";
import { scrollToId } from "@/hooks/useRevealObserver";

interface Suggestion {
  title: string;
  section: string;
  anchor: string;
}

function buildIndex(): Suggestion[] {
  const out: Suggestion[] = [];
  hotList.forEach((h) => out.push({ title: h.title, section: "今日热榜", anchor: "hot" }));
  focusEvents.forEach((f) => out.push({ title: f.title, section: "焦点事件", anchor: "focus" }));
  picks.forEach((p) => out.push({ title: p.title, section: "精选看点", anchor: "picks" }));
  return out;
}

export default function SearchBox({ autoFocus = false }: { autoFocus?: boolean }) {
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const index = useMemo(buildIndex, []);
  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return index.filter((it) => it.title.toLowerCase().includes(kw)).slice(0, 6);
  }, [keyword, index]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const pick = (anchor: string) => {
    setOpen(false);
    setKeyword("");
    scrollToId(anchor);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2.5 transition-colors focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-200">
        <i className="ri-search-line text-base text-foreground-500" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          type="text"
          placeholder="搜搜今天的热点话题…"
          aria-label="搜索热点话题"
          className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
        {keyword ? (
          <button type="button" aria-label="清空" onClick={() => { setKeyword(""); inputRef.current?.focus(); }} className="text-foreground-400 hover:text-foreground-700 cursor-pointer">
            <i className="ri-close-circle-fill text-base" />
          </button>
        ) : null}
      </div>

      {open && keyword.trim() ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft">
          {results.length > 0 ? (
            <ul className="py-1.5">
              {results.map((it, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => pick(it.anchor)}
                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-search-line mt-1 text-sm text-primary-500" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900">{it.title}</span>
                      <span className="mt-0.5 block text-xs text-foreground-500">{it.section}</span>
                    </span>
                    <i className="ri-arrow-right-up-line mt-1 text-sm text-foreground-400" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-5 text-center text-sm text-foreground-500">没有找到相关话题，换个关键词试试～</p>
          )}
        </div>
      ) : null}
    </div>
  );
}